import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loginVendorUser, registerVendorUser } from "../state/slices/vendorSlice";
import ImageCropUploader from "./ImageCropUploader";
import { checkFieldsForProfanity } from "../helper/profanityFilter";
import TermsModal from "./TermsModal";

const US_STATES = [
    { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
    { value: 'DC', label: 'District of Columbia' }, { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' }, { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' }, { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' }, { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' }, { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' }, { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' }, { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' }, { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
];

const VendorLogin = () => {
    const [registerLoading, setRegisterLoading] = useState(false);
    const [loginLoading, setLoginLoading] = useState(false);
    const [registerError, setRegisterError] = useState("");
    const [loginError, setLoginError] = useState("");
    const [registerSuccess, setRegisterSuccess] = useState("");
    const [showTermsModal, setShowTermsModal] = useState(false);

    const [loginData, setLoginData] = useState({ email: "", password: "" });
    const [showLoginPassword, setShowLoginPassword] = useState(false);

    const [registerData, setRegisterData] = useState({
        vendor_name: "",
        vendor_description: "",
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        confirmPassword: "",
        phone_number: "",
        website: "",
        address: "",
        apt_number: "",
        city: "",
        state: "",
        zipcode: "",
        mailing_address: "",
        mailing_apt_number: "",
        mailing_city: "",
        mailing_state: "",
        mailing_zipcode: "",
        image: "",
        banner_image: "",
    });

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleLoginChange = (e) =>
        setLoginData({ ...loginData, [e.target.name]: e.target.value });

    const formatPhoneNumber = (value) => {
        const digits = value.replace(/\D/g, '').slice(0, 10);
        const len = digits.length;
        if (len === 0) return '';
        if (len < 3) return `(${digits}`;
        if (len === 3) return `(${digits})`;
        if (len <= 6) return `(${digits.slice(0, 3)})-${digits.slice(3)}`;
        return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
    };

    const handleRegisterChange = (e) => {
        const { name, value } = e.target;
        setRegisterData({ ...registerData, [name]: name === 'phone_number' ? formatPhoneNumber(value) : value });
    };

    const openTermsModal = () => {
        if (registerData.password !== registerData.confirmPassword) {
            setRegisterError("Passwords do not match.");
            return;
        }
        const profanityError = checkFieldsForProfanity({
            "Vendor Name": registerData.vendor_name,
            "Vendor Description": registerData.vendor_description,
            "First Name": registerData.first_name,
            "Last Name": registerData.last_name,
            "Address": registerData.address,
            "City": registerData.city,
            "Website": registerData.website,
        });
        if (profanityError) { setRegisterError(profanityError); return; }
        setRegisterError("");
        setShowTermsModal(true);
    };

    const handleVendorRegisterAfterTerms = async () => {
        try {
            setRegisterLoading(true);
            setRegisterError("");
            setRegisterSuccess("");
            await dispatch(registerVendorUser({
                ...registerData,
                accepted_terms: true,
                terms_version: "2026-06-12",
            })).unwrap();
            navigate("/vendor-dashboard");
        } catch (err) {
            setRegisterError(err.message);
        } finally {
            setRegisterLoading(false);
        }
    };

    const handleVendorLogin = async (e) => {
        e.preventDefault();
        try {
            setLoginLoading(true);
            setLoginError("");
            await dispatch(loginVendorUser(loginData)).unwrap();
            navigate("/vendor-dashboard");
        } catch (err) {
            setLoginError(err.message);
        } finally {
            setLoginLoading(false);
        }
    };

    return (
        <>
        <section style={{ background: "#f7f8fc", minHeight: "100vh" }} className="py-80">
            <style>{`
                .auth-wrap { position: relative; }
                .auth-icon {
                    position: absolute;
                    left: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    font-size: 16px;
                    color: #c0c8d8;
                    pointer-events: none;
                    z-index: 1;
                }
                .auth-field {
                    width: 100%;
                    padding: 11px 14px 11px 40px;
                    border: 1.5px solid #e9ecf0;
                    border-radius: 10px;
                    font-size: 14px;
                    color: #1a1a2e;
                    background: #fff;
                    transition: border-color 0.15s, box-shadow 0.15s;
                    outline: none;
                    appearance: none;
                }
                .auth-field:focus {
                    border-color: #2575fc;
                    box-shadow: 0 0 0 3px rgba(37,117,252,0.1);
                }
                .auth-field::placeholder { color: #c4cdd8; }
                .auth-label {
                    display: block;
                    font-size: 11.5px;
                    font-weight: 700;
                    color: #6b7280;
                    letter-spacing: 0.5px;
                    text-transform: uppercase;
                    margin-bottom: 7px;
                }
                .auth-card {
                    background: #fff;
                    border-radius: 20px;
                    box-shadow: 0 4px 32px rgba(0,0,0,0.07);
                    border: 1px solid rgba(0,0,0,0.04);
                }
                .auth-divider {
                    height: 1px;
                    background: linear-gradient(90deg, transparent, #f0f0f0 20%, #f0f0f0 80%, transparent);
                    margin: 24px 0;
                }
                .auth-submit {
                    width: 100%;
                    padding: 13px 24px;
                    border: none;
                    border-radius: 11px;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: opacity 0.15s, transform 0.1s;
                    letter-spacing: 0.2px;
                }
                .auth-submit:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
                .auth-submit:disabled { opacity: 0.55; cursor: not-allowed; }
                .auth-error {
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    color: #dc2626;
                    border-radius: 10px;
                    padding: 11px 14px;
                    font-size: 13.5px;
                    margin-bottom: 18px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .auth-success {
                    background: #f0fdf4;
                    border: 1px solid #bbf7d0;
                    color: #16a34a;
                    border-radius: 10px;
                    padding: 11px 14px;
                    font-size: 13.5px;
                    margin-bottom: 18px;
                }
                .auth-section-tag {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: #eef4ff;
                    color: #2575fc;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 0.8px;
                    text-transform: uppercase;
                    padding: 5px 12px;
                    border-radius: 99px;
                    margin-bottom: 12px;
                }
                .upload-card {
                    background: #f8f9fb;
                    border: 1.5px dashed #e0e4eb;
                    border-radius: 12px;
                    padding: 20px 20px 16px;
                    transition: border-color 0.15s;
                }
                .upload-card:hover { border-color: #2575fc; }
                .auth-section-heading {
                    font-size: 13px;
                    font-weight: 700;
                    color: #374151;
                    letter-spacing: 0.3px;
                    margin-bottom: 16px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
            `}</style>

            <div className="container container-lg">
                <div className="row gy-4">

                    {/* ── VENDOR REGISTER ── */}
                    <div className="col-xl-6">
                        <form onSubmit={(e) => e.preventDefault()} className="h-100">
                            <div className="auth-card p-40 h-100">

                                <div className="mb-28">
                                    <span className="auth-section-tag">
                                        <i className="ph ph-storefront" />
                                        Vendor Account
                                    </span>
                                    <h4 className="fw-bold mb-6" style={{ fontSize: 24, color: "#0f172a" }}>
                                        Register as a Vendor
                                    </h4>
                                    <p style={{ fontSize: 14, color: "#94a3b8", margin: 0 }}>
                                        List your business and start reaching customers
                                    </p>
                                </div>

                                {/* ── Vendor Information ── */}
                                <p className="auth-section-heading">
                                    <i className="ph ph-storefront" style={{ color: "#2575fc" }} />
                                    Vendor Information
                                </p>

                                <div className="mb-20">
                                    <label className="auth-label">
                                        Vendor Name <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <div className="auth-wrap">
                                        <i className="ph ph-storefront auth-icon" />
                                        <input
                                            type="text"
                                            className="auth-field"
                                            name="vendor_name"
                                            value={registerData.vendor_name}
                                            onChange={handleRegisterChange}
                                            placeholder="Example: Fun Card Media"
                                            maxLength={35}
                                        />
                                    </div>
                                    <p className="mb-0 mt-4" style={{ fontSize: 11.5, color: registerData.vendor_name.length >= 35 ? "#ef4444" : "#94a3b8", textAlign: "right" }}>
                                        {registerData.vendor_name.length}/35
                                    </p>
                                </div>

                                <div className="mb-20">
                                    <label className="auth-label">Vendor Description</label>
                                    <textarea
                                        className="auth-field"
                                        style={{ paddingLeft: 14, resize: "vertical", minHeight: 90 }}
                                        name="vendor_description"
                                        value={registerData.vendor_description}
                                        onChange={handleRegisterChange}
                                        placeholder="Describe your business..."
                                        rows="3"
                                        maxLength={255}
                                    />
                                    <p className="mb-0 mt-4" style={{ fontSize: 11.5, color: registerData.vendor_description.length >= 255 ? "#ef4444" : "#94a3b8", textAlign: "right" }}>
                                        {registerData.vendor_description.length}/255
                                    </p>
                                </div>

                                <div className="row g-3 mb-20">
                                    <div className="col-md-6">
                                        <label className="auth-label">Website</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-globe auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="website"
                                                value={registerData.website}
                                                onChange={handleRegisterChange}
                                                placeholder="https://yourwebsite.com"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="auth-label">Phone Number</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-phone auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="phone_number"
                                                value={registerData.phone_number}
                                                onChange={handleRegisterChange}
                                                placeholder="(555)-555-5555"
                                            />
                                        </div>
                                        {registerData.phone_number && (
                                            <p className="mb-0 mt-4 d-flex align-items-center gap-6" style={{
                                                fontSize: 12,
                                                color: registerData.phone_number.replace(/\D/g, '').length === 10 ? "#10b981" : "#ef4444",
                                                fontWeight: 500,
                                            }}>
                                                <i className={`ph ${registerData.phone_number.replace(/\D/g, '').length === 10 ? "ph-check-circle" : "ph-warning-circle"}`} style={{ fontSize: 14 }} />
                                                {registerData.phone_number.replace(/\D/g, '').length === 10 ? "Valid phone number" : `${registerData.phone_number.replace(/\D/g, '').length}/10 digits`}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Vendor Icon Photo */}
                                <div className="mb-20">
                                    <label className="auth-label" style={{ marginBottom: 10 }}>Vendor Icon Photo</label>
                                    <div className="upload-card">
                                        <div className="d-flex align-items-center gap-10 mb-14">
                                            <div className="d-flex align-items-center justify-content-center rounded-10 flex-shrink-0" style={{ width: 36, height: 36, background: "#eef4ff" }}>
                                                <i className="ph ph-image" style={{ fontSize: 18, color: "#2575fc" }} />
                                            </div>
                                            <div>
                                                <p className="fw-semibold mb-0" style={{ fontSize: 13, color: "#374151" }}>Vendor Icon</p>
                                                <p className="mb-0" style={{ fontSize: 12, color: "#94a3b8" }}>Appears as your vendor avatar on cards across the site</p>
                                            </div>
                                        </div>
                                        <ImageCropUploader
                                            uploadUrl={`${process.env.REACT_APP_API_URL}/api/upload/vendor-image`}
                                            onUpload={(url) => setRegisterData((prev) => ({ ...prev, image: url }))}
                                            currentImageUrl={registerData.image}
                                        />
                                    </div>
                                </div>

                                {/* Vendor Banner Photo */}
                                <div className="mb-20">
                                    <label className="auth-label" style={{ marginBottom: 10 }}>Vendor Card Background Photo</label>
                                    <div className="upload-card">
                                        <div className="d-flex align-items-center gap-10 mb-14">
                                            <div className="d-flex align-items-center justify-content-center rounded-10 flex-shrink-0" style={{ width: 36, height: 36, background: "#f5f3ff" }}>
                                                <i className="ph ph-images" style={{ fontSize: 18, color: "#8b5cf6" }} />
                                            </div>
                                            <div>
                                                <p className="fw-semibold mb-0" style={{ fontSize: 13, color: "#374151" }}>Banner Image</p>
                                                <p className="mb-0" style={{ fontSize: 12, color: "#94a3b8" }}>Background image displayed on your vendor listing card</p>
                                            </div>
                                        </div>
                                        <ImageCropUploader
                                            uploadUrl={`${process.env.REACT_APP_API_URL}/api/upload/vendor-image`}
                                            onUpload={(url) => setRegisterData((prev) => ({ ...prev, banner_image: url }))}
                                            currentImageUrl={registerData.banner_image}
                                        />
                                    </div>
                                </div>

                                <div className="auth-divider" />

                                {/* ── Owner Login Information ── */}
                                <p className="auth-section-heading">
                                    <i className="ph ph-user-circle" style={{ color: "#2575fc" }} />
                                    Owner Login Information
                                </p>

                                <div className="row g-3 mb-20">
                                    <div className="col-6">
                                        <label className="auth-label">
                                            First Name <span style={{ color: "#ef4444" }}>*</span>
                                        </label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-user auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="first_name"
                                                value={registerData.first_name}
                                                onChange={handleRegisterChange}
                                                placeholder="John"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <label className="auth-label">
                                            Last Name <span style={{ color: "#ef4444" }}>*</span>
                                        </label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-user auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="last_name"
                                                value={registerData.last_name}
                                                onChange={handleRegisterChange}
                                                placeholder="Doe"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-20">
                                    <label className="auth-label">
                                        Email Address <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <div className="auth-wrap">
                                        <i className="ph ph-envelope auth-icon" />
                                        <input
                                            type="email"
                                            className="auth-field"
                                            name="email"
                                            value={registerData.email}
                                            onChange={handleRegisterChange}
                                            placeholder="vendor@example.com"
                                        />
                                    </div>
                                </div>

                                <div className="row g-3 mb-20">
                                    <div className="col-md-6">
                                        <label className="auth-label">
                                            Password <span style={{ color: "#ef4444" }}>*</span>
                                        </label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-lock auth-icon" />
                                            <input
                                                type="password"
                                                className="auth-field"
                                                name="password"
                                                value={registerData.password}
                                                onChange={handleRegisterChange}
                                                placeholder="••••••••"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="auth-label">
                                            Re-enter Password <span style={{ color: "#ef4444" }}>*</span>
                                        </label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-lock-key auth-icon" />
                                            <input
                                                type="password"
                                                className="auth-field"
                                                name="confirmPassword"
                                                value={registerData.confirmPassword}
                                                onChange={handleRegisterChange}
                                                placeholder="••••••••"
                                                style={{
                                                    borderColor: registerData.confirmPassword
                                                        ? registerData.password === registerData.confirmPassword ? "#10b981" : "#ef4444"
                                                        : "",
                                                }}
                                            />
                                        </div>
                                        {registerData.confirmPassword && (
                                            <p className="mb-0 mt-6 d-flex align-items-center gap-6" style={{
                                                fontSize: 12,
                                                color: registerData.password === registerData.confirmPassword ? "#10b981" : "#ef4444",
                                                fontWeight: 500,
                                            }}>
                                                <i className={`ph ${registerData.password === registerData.confirmPassword ? "ph-check-circle" : "ph-x-circle"}`} style={{ fontSize: 14 }} />
                                                {registerData.password === registerData.confirmPassword ? "Passwords match" : "Passwords do not match"}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="auth-divider" />

                                {/* ── Vendor Address ── */}
                                <p className="auth-section-heading">
                                    <i className="ph ph-map-pin" style={{ color: "#2575fc" }} />
                                    Vendor Address
                                </p>

                                <div className="mb-20">
                                    <label className="auth-label">Address</label>
                                    <div className="auth-wrap">
                                        <i className="ph ph-map-pin auth-icon" />
                                        <input
                                            type="text"
                                            className="auth-field"
                                            name="address"
                                            value={registerData.address}
                                            onChange={handleRegisterChange}
                                            placeholder="123 Main Street"
                                        />
                                    </div>
                                </div>

                                <div className="row g-3 mb-20">
                                    <div className="col-4">
                                        <label className="auth-label">Apt #</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-door auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="apt_number"
                                                value={registerData.apt_number}
                                                onChange={handleRegisterChange}
                                                placeholder="Apt 4B"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-8">
                                        <label className="auth-label">City</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-buildings auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="city"
                                                value={registerData.city}
                                                onChange={handleRegisterChange}
                                                placeholder="City"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-3 mb-20">
                                    <div className="col-4">
                                        <label className="auth-label">State</label>
                                        <select
                                            className="auth-field"
                                            name="state"
                                            value={registerData.state}
                                            onChange={handleRegisterChange}
                                            style={{ paddingLeft: 14, appearance: 'auto' }}
                                        >
                                            <option value="">State</option>
                                            {US_STATES.map(s => (
                                                <option key={s.value} value={s.value}>{s.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-8">
                                        <label className="auth-label">ZIP Code</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-map-trifold auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="zipcode"
                                                value={registerData.zipcode}
                                                onChange={handleRegisterChange}
                                                placeholder="34145"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="auth-divider" />

                                {/* ── Mailing Address ── */}
                                <p className="auth-section-heading">
                                    <i className="ph ph-envelope-simple" style={{ color: "#2575fc" }} />
                                    Mailing Address
                                </p>

                                <div className="mb-20">
                                    <label className="auth-label">Mailing Address</label>
                                    <div className="auth-wrap">
                                        <i className="ph ph-map-pin auth-icon" />
                                        <input
                                            type="text"
                                            className="auth-field"
                                            name="mailing_address"
                                            value={registerData.mailing_address}
                                            onChange={handleRegisterChange}
                                            placeholder="Mailing Address"
                                        />
                                    </div>
                                </div>

                                <div className="row g-3 mb-20">
                                    <div className="col-4">
                                        <label className="auth-label">Apt #</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-door auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="mailing_apt_number"
                                                value={registerData.mailing_apt_number}
                                                onChange={handleRegisterChange}
                                                placeholder="Apt 4B"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-8">
                                        <label className="auth-label">City</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-buildings auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="mailing_city"
                                                value={registerData.mailing_city}
                                                onChange={handleRegisterChange}
                                                placeholder="Mailing City"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-3 mb-28">
                                    <div className="col-4">
                                        <label className="auth-label">State</label>
                                        <select
                                            className="auth-field"
                                            name="mailing_state"
                                            value={registerData.mailing_state}
                                            onChange={handleRegisterChange}
                                            style={{ paddingLeft: 14, appearance: 'auto' }}
                                        >
                                            <option value="">State</option>
                                            {US_STATES.map(s => (
                                                <option key={s.value} value={s.value}>{s.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-8">
                                        <label className="auth-label">Zipcode</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-map-trifold auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="mailing_zipcode"
                                                value={registerData.mailing_zipcode}
                                                onChange={handleRegisterChange}
                                                placeholder="Mailing Zipcode"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {registerError && (
                                    <div className="auth-error">
                                        <i className="ph ph-warning-circle" style={{ fontSize: 16, flexShrink: 0 }} />
                                        {registerError}
                                    </div>
                                )}
                                {registerSuccess && <div className="auth-success">{registerSuccess}</div>}

                                <button
                                    type="button"
                                    className="auth-submit"
                                    style={{ background: "#6B9FD4", color: "#fff" }}
                                    disabled={registerLoading || (registerData.confirmPassword.length > 0 && registerData.password !== registerData.confirmPassword)}
                                    onClick={openTermsModal}
                                >
                                    {registerLoading ? "Creating Account..." : "Create Vendor Account"}
                                </button>

                            </div>
                        </form>
                    </div>

                    {/* ── VENDOR LOGIN ── */}
                    <div className="col-xl-6">
                        <form onSubmit={handleVendorLogin}>
                            <div className="auth-card p-40">

                                {/* Icon header */}
                                <div className="text-center mb-36">
                                    <div
                                        className="d-inline-flex align-items-center justify-content-center rounded-circle mb-20"
                                        style={{
                                            width: 64,
                                            height: 64,
                                            background: "linear-gradient(135deg, #0d1f4f 0%, #1a3a8f 60%, #2575fc 100%)",
                                            boxShadow: "0 8px 24px rgba(37,117,252,0.3)",
                                        }}
                                    >
                                        <i className="ph ph-storefront" style={{ fontSize: 28, color: "#fff" }} />
                                    </div>
                                    <span className="auth-section-tag d-inline-flex">
                                        <i className="ph ph-sign-in" />
                                        Vendor Portal
                                    </span>
                                    <h4 className="fw-bold mb-6" style={{ fontSize: 24, color: "#0f172a" }}>
                                        Welcome Back
                                    </h4>
                                    <p style={{ fontSize: 14, color: "#94a3b8", margin: 0 }}>
                                        Sign in to your vendor dashboard
                                    </p>
                                </div>

                                {/* Email */}
                                <div className="mb-20">
                                    <label className="auth-label">
                                        Email Address <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <div className="auth-wrap">
                                        <i className="ph ph-envelope auth-icon" />
                                        <input
                                            type="email"
                                            className="auth-field"
                                            name="email"
                                            value={loginData.email}
                                            onChange={handleLoginChange}
                                            placeholder="vendor@example.com"
                                        />
                                    </div>
                                </div>

                                {/* Password */}
                                <div className="mb-28">
                                    <label className="auth-label">Password</label>
                                    <div className="auth-wrap position-relative">
                                        <i className="ph ph-lock auth-icon" />
                                        <input
                                            type={showLoginPassword ? "text" : "password"}
                                            className="auth-field"
                                            name="password"
                                            value={loginData.password}
                                            onChange={handleLoginChange}
                                            placeholder="••••••••"
                                        />
                                        <span
                                            onClick={() => setShowLoginPassword(v => !v)}
                                            className={`position-absolute top-50 end-0 me-14 translate-middle-y cursor-pointer ph ${showLoginPassword ? "ph-eye" : "ph-eye-slash"}`}
                                            style={{ color: "#c0c8d8", fontSize: 16 }}
                                        />
                                    </div>
                                </div>

                                {/* Remember me */}
                                <div
                                    className="d-flex align-items-center gap-10 mb-24 p-14 rounded-10"
                                    style={{ background: "#f8f9fb", border: "1px solid #f0f0f0" }}
                                >
                                    <input
                                        className="form-check-input m-0"
                                        type="checkbox"
                                        id="remember"
                                        style={{ width: 17, height: 17, cursor: "pointer", accentColor: "#2575fc" }}
                                    />
                                    <label
                                        className="form-check-label fw-medium"
                                        htmlFor="remember"
                                        style={{ fontSize: 14, color: "#4b5563", cursor: "pointer", marginBottom: 0 }}
                                    >
                                        Remember me
                                    </label>
                                </div>

                                {loginError && (
                                    <div className="auth-error">
                                        <i className="ph ph-warning-circle" style={{ fontSize: 16, flexShrink: 0 }} />
                                        {loginError}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="auth-submit mb-20"
                                    style={{
                                        background: "linear-gradient(135deg, #0d1f4f 0%, #2575fc 100%)",
                                        color: "#fff",
                                        boxShadow: "0 4px 16px rgba(37,117,252,0.3)",
                                    }}
                                    disabled={loginLoading}
                                >
                                    {loginLoading ? "Signing In..." : "Sign In to Dashboard"}
                                </button>

                                <div className="text-center">
                                    <Link
                                        to="#"
                                        style={{ fontSize: 13, color: "#94a3b8", fontWeight: 600 }}
                                        className="hover-text-decoration-underline"
                                    >
                                        Forgot your password?
                                    </Link>
                                </div>

                            </div>
                        </form>
                    </div>

                </div>
            </div>
        </section>
        <TermsModal
            isOpen={showTermsModal}
            onClose={() => setShowTermsModal(false)}
            onAgree={handleVendorRegisterAfterTerms}
            accountType="Vendor Account"
        />
        </>
    );
};

export default VendorLogin;
